import { GetStaticPaths, GetStaticProps } from 'next'
import { NextSeo } from 'next-seo'

import ChannelGuard from '@/guards/ChannelGuard'
import AssembleLayout from '@/layouts/AssembleLayout'
import MainLayout from '@/layouts/MainLayout'
import { channelMiddleware, siteMiddleware } from '@/lib/middlewares'
import { urlHostResolver } from '@/lib/utils'
import { noIndex } from '@/lib/utils/next-seo.config'
import { Channel as ChannelType, Site as SiteType } from '@/types'
import ChannelView from '@/views/sites/ChannelView'

interface Data {
  site?: SiteType
  channel?: ChannelType
}

interface Props {
  initialData?: Data
}

export default function ChannelPage({ initialData }: Props) {
  const { site: siteData, channel: channelData } = initialData || {}

  // SEO
  const urlSEO = siteData?.domain
    ? `${siteData.domain}/${channelData?.slug}`
    : urlHostResolver(siteData?.subdomain, `/${channelData?.slug}`)

  const commonSEOProps = {
    title: `${channelData?.title || 'Canal'}${siteData ? ` | ${siteData.title}` : ''}`,
    description: `${channelData?.title || 'Canal'}${siteData ? ` | ${siteData.title}` : ''}`
  }

  return (
    <AssembleLayout
      Layout={MainLayout}
      layoutProps={{ displayCover: true }}
      View={ChannelView}
      Guard={ChannelGuard}
      initialData={{ initialData }}>
      <NextSeo
        canonical={urlSEO}
        {...commonSEOProps}
        openGraph={{
          ...commonSEOProps,
          url: urlSEO,
          images: [
            { url: (channelData?.cover || channelData?.logo) as string, alt: commonSEOProps.title }
          ]
        }}
        {...(channelData?.privacy !== 'public' ? noIndex : {})}
      />
    </AssembleLayout>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  return { paths: [], fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps = async context => {
  const { data: siteData } = await siteMiddleware(context)
  const { data: channelData, error: channelDataError } = await channelMiddleware(context)

  return channelDataError?.response?.status === 404
    ? { notFound: true, revalidate: 60 }
    : { props: { initialData: { site: siteData, channel: channelData } }, revalidate: 60 }
} // revalidate time in seconds
